import { Injectable } from '@angular/core';
import {
  Firestore, collection, doc, getDoc,
  query, where, collectionData,
  updateDoc,
} from '@angular/fire/firestore';
import { BehaviorSubject, Observable, from, of, throwError } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { Being } from '../../interfaces/being.model';
import { AnonymousUserService } from '../user services/anonymous-user.service';
import { Functions, httpsCallable } from '@angular/fire/functions';



@Injectable({ providedIn: 'root' })




export class BeingService {

  // Local cache of *which* beings this device watches. Mirrors the
  // IndexedDB pattern in UserService — but only the pointer list is local.
  private db: IDBDatabase | null = null;
  private readonly DB_NAME = 'being_database';
  private readonly STORE_NAME = 'being_store';
  private readonly DB_VERSION = 1;
  private readonly OWNED_KEY = 'owned_being_ids';

  // Zoom visibility threshold
  private readonly MIN_VISIBLE_ZOOM = 10; // ~1km personal space; z<14 = invisible

  private ownedBeingIdsSubject = new BehaviorSubject<string[]>([]);
  public ownedBeingIds$ = this.ownedBeingIdsSubject.asObservable();

  constructor(
    private _firestore: Firestore,
    private _anonymousUserService: AnonymousUserService,
    private _functions: Functions
  ) {
    this.initDatabase().subscribe();
  }

  // ---------------------------------------------------------------
  // Local store 
  // ---------------------------------------------------------------

  private initDatabase(): Observable<string[]> {
    return from(new Promise<string[]>((resolve) => {
      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

      request.onerror = () => {
        console.error('🧬 Error opening being database');
        resolve([]); // Fail gracefully, sync from Firestore will catch it
      };

      request.onsuccess = (event) => {
        this.db = (event.target as IDBOpenDBRequest).result;
        resolve([]);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.STORE_NAME)) {
          db.createObjectStore(this.STORE_NAME, { keyPath: 'key' });
        }
      };
    })).pipe(
      switchMap(() => this.syncOwnedBeingsFromFirestore()),
      tap(ids => this.ownedBeingIdsSubject.next(ids))
    );
  }

  private syncOwnedBeingsFromFirestore(): Observable<string[]> {
    return of(this._anonymousUserService.currentUserId).pipe(
      take(1),
      switchMap(userId => {
        if (!userId) return of([]);

        const q = query(
          collection(this._firestore, 'beings'),
          where('creatorDeviceId', '==', userId)
        );
        return collectionData(q, { idField: 'id' }) as Observable<Being[]>;
      }),
      switchMap(beings => {
        const ids = beings.map(b => b.id);
        return this.persistOwnedBeingIds(ids);
      }),
      catchError(err => {
        console.warn('🧬 Could not sync from Firestore:', err);
        return of([]);
      })
    );
  }


  private persistOwnedBeingIds(ids: string[]): Observable<string[]> {
    if (!this.db) {
      return this.initDatabase().pipe(
        switchMap(() => this.persistOwnedBeingIds(ids))
      );
    }

    return from(new Promise<string[]>((resolve, reject) => {
      const tx = this.db!.transaction(this.STORE_NAME, 'readwrite');
      const request = tx.objectStore(this.STORE_NAME)
        .put({ key: this.OWNED_KEY, value: ids });

      request.onerror = () => reject('Failed to persist owned beings');
      request.onsuccess = () => resolve(ids);
    })).pipe(
      tap(persisted => this.ownedBeingIdsSubject.next(persisted))
    );
  }


  // ---------------------------------------------------------------
  // Birth
  // ---------------------------------------------------------------

  createBeing(): Observable<Being> {
    return this.getBirthCoordinates().pipe(
      switchMap(coords => {
        const call = httpsCallable<
          { lat?: number; lng?: number; deviceId?: string },
          Being
        >(this._functions, 'birthBeing');

        return from(call({
          ...coords,
          deviceId: this._anonymousUserService.currentUserId
        }));
      }),
      map(result => result.data),
      switchMap(being =>
        this.registerOwnership(being.id).pipe(map(() => being))
      ),
      catchError(err => {
        console.error('🧬 Birth failed:', err?.code, err?.message);
        return throwError(() => err);
      })
    );
  }


  private getBirthCoordinates(): Observable<{ lat?: number; lng?: number }> {
    return from(new Promise<GeolocationCoordinates>((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not available'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        pos => resolve(pos.coords),
        err => reject(err),
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    })).pipe(
      map(c => ({ lat: c.latitude, lng: c.longitude })),
      catchError(err => {
        console.warn('🧬 Location unavailable, server will choose:', err);
        return of({});
      })
    );
  }




  getCreatedBeings(): Observable<Being[]> {
    return collectionData(
      collection(this._firestore, 'beings'),
      { idField: 'id' }
    ) as Observable<Being[]>;
  }




  // ---------------------------------------------------------------
  // Reads
  // ---------------------------------------------------------------

  getBeing(id: string): Observable<Being | null> {
    return from(getDoc(doc(this._firestore, 'beings', id))).pipe(
      map(snap => snap.exists() ? snap.data() as Being : null),
      catchError(err => {
        console.error('🧬 Error fetching being:', err);
        return of(null);
      })
    );
  }

  renameBeing(beingId: string, name: string): Observable<void> {
    const trimmed = name.trim();
    if (!trimmed || trimmed.length > 24) {
      return throwError(() => new Error('Invalid name'));
    }

    const ref = doc(this._firestore, 'beings', beingId);
    return from(updateDoc(ref, { name: trimmed })).pipe(
      tap(() => console.log('🧬 Renamed:', beingId, '→', trimmed)),
      catchError(err => {
        console.error('🧬 Rename failed:', err);
        return throwError(() => err);
      })
    );
  }

  /** Live feed of this device's beings — drives the "what happened while
   *  I was away" surface described in §12. */
  getMyBeings(): Observable<Being[]> {
    return of(this._anonymousUserService.currentUserId).pipe(
      take(1),
      switchMap(deviceId => {
        if (!deviceId) return of([]);

        const q = query(
          collection(this._firestore, 'beings'),
          where('creatorDeviceId', '==', deviceId)
        );
        return collectionData(q, { idField: 'id' }) as Observable<Being[]>;
      }),
      catchError(err => {
        console.error('🧬 Error streaming beings:', err);
        return of([]);
      })
    );
  }


  private registerOwnership(beingId: string): Observable<string[]> {
    const current = this.ownedBeingIdsSubject.getValue();
    if (current.includes(beingId)) return of(current);
    return this.persistOwnedBeingIds([...current, beingId]);
  }

  // ---------------------------------------------------------------
  // Visibility
  // ---------------------------------------------------------------

  /**
   * Determine if a being should be rendered at the current map zoom level.
   * Beings are "invisible from space" — only visible on close inspection.
   *
   * Z14 (1:16,000) ≈ city/neighborhood — beings become visible here
   * Z0-13 — beings hidden (world/continent/country view)
   */
  isBeingVisibleAtZoom(zoom: number): boolean {
    return zoom >= this.MIN_VISIBLE_ZOOM;
  }

  /**
   * Get the visibility threshold — useful for debugging or dynamic config.
   */
  getVisibilityThreshold(): number {
    return this.MIN_VISIBLE_ZOOM;
  }

  // ---------------------------------------------------------------
  // Generators / helpers
  // ---------------------------------------------------------------

  /**
   * Names are phonetic, not descriptive — a being shouldn't be labelled
   * with knowledge it hasn't acquired (§16).
   */
  generateBeingName(): string {
    const onsets = ['k', 't', 'm', 'n', 's', 'r', 'v', 'l', 'th', 'sh', 'z', 'd'];
    const nuclei = ['a', 'e', 'i', 'o', 'u', 'ae', 'ei', 'ou'];
    const codas = ['', '', 'n', 'r', 'l', 's', 'm', 'k'];
    const syllables = 2 + Math.floor(Math.random() * 2);

    let name = '';
    for (let i = 0; i < syllables; i++) {
      name += this.pick(onsets) + this.pick(nuclei) + this.pick(codas);
    }
    return name.charAt(0).toUpperCase() + name.slice(1);
  }

  private generateBeingId(): string {
    return crypto.randomUUID
      ? crypto.randomUUID()
      : 'b_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
  }

  private pick<T>(arr: T[]): T {
    return arr[Math.floor(Math.random() * arr.length)];
  }

  /** Box-Muller — flat Math.random() makes every founder mediocre. */
  private gaussian(mean: number, stdDev: number): number {
    const u = 1 - Math.random();
    const v = Math.random();
    return mean + stdDev * Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
  }

  private clamp(value: number, min = 0, max = 1): number {
    return Math.min(max, Math.max(min, value));
  }

  private clearLocalStorage(): void {
    try {
      localStorage.removeItem(this.OWNED_KEY);
      console.log('🧬 Cleared localStorage (fresh database init)');
    } catch (err) {
      console.warn('🧬 Could not clear localStorage:', err);
    }
  }
}